import { type CSSProperties, type ReactNode } from 'react';
import { c } from '../theme';

export function Wordmark({ style }: { style?: CSSProperties }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 3,
        fontStretch: '125%',
        fontWeight: 700,
        fontSize: 15,
        letterSpacing: '0.04em',
        color: c.red,
        ...style,
      }}
    >
      READYBUY
      <span style={{ fontSize: 7, position: 'relative', top: -6 }}>™</span>
    </div>
  );
}

/** The 390px cream column every screen lives in. */
export function Shell({
  children,
  padded = true,
  style,
}: {
  children: ReactNode;
  padded?: boolean;
  style?: CSSProperties;
}) {
  return (
    <div
      className="rb-stage"
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        background: c.page,
        padding: '40px 0 60px',
      }}
    >
      <div
        className="rb-shell"
        style={{
          width: 390,
          maxWidth: '100%',
          minHeight: 844,
          background: c.screen,
          color: c.ink,
          borderRadius: '40px 40px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          padding: padded ? '48px var(--gx) 40px' : 0,
          ...style,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function MicroLabel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ fontSize: 10, letterSpacing: '0.2em', color: c.ink5, ...style }}>{children}</div>
  );
}

export function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <span style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 600, color: c.ink6 }}>{children}</span>
  );
}

export function Headline({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 40, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.02em' }}>{children}</div>
  );
}

export function Body({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ fontSize: 14, lineHeight: 1.5, color: c.ink6, maxWidth: 300, textWrap: 'pretty', ...style }}>
      {children}
    </div>
  );
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
  type = 'button',
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  style?: CSSProperties;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'block',
        width: '100%',
        boxSizing: 'border-box',
        padding: '19px 0',
        borderRadius: 999,
        background: disabled ? c.hairline : c.red,
        color: disabled ? c.ink4 : c.cream,
        fontSize: 15,
        fontWeight: 600,
        textAlign: 'center',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background .15s ease',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/** Small outlined red pill — DOWNLOAD REPORT, TAP TO OPEN, SIGN OUT. */
export function OutlinePill({
  children,
  onClick,
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  style?: CSSProperties;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 10,
        letterSpacing: '0.12em',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        border: `1px solid ${c.redLine}`,
        padding: '8px 14px',
        borderRadius: 999,
        color: c.red,
        background: 'transparent',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function Notice({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'error' }) {
  return (
    <div
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        fontSize: 11,
        lineHeight: 1.5,
        fontWeight: 500,
        letterSpacing: '0.02em',
        color: tone === 'error' ? c.red : c.ink6,
        background: tone === 'error' ? c.redSoft : c.creamDeep,
        borderRadius: 14,
        padding: '12px 16px',
      }}
    >
      {children}
    </div>
  );
}
